// Who wrote a message in a thread, when the chat row cannot say (s175).
//
// The web's copy of the shared KMP rule in
// `mobile/shared/.../domain/AuthorNames.kt` — keep the two saying the same thing.
//
// A thread names its authors from the chat row's counterpart rollup, which the
// server bounds at 8 (`messaging.ex` `@member_rollup_limit`). In a group of ten
// the ninth voice read "Unknown" on all four surfaces. The full roster
// (`GET /chats/:id/members`) fills the gap, and is asked for only when a
// message from someone the rollup cannot name is in the thread:
//
//  1. The rollup answers first; the roster only fills what it leaves out.
//  2. An unnamed sender is asked about ONCE. A member who has LEFT is absent
//     from the roster too, and without this every new message would refetch it.
//  3. Our own messages and system rows are never asked about.

import type { ChatCounterpart, Message } from "./types.ts";

/** The one naming rule: display name, else @handle, else nothing usable. */
export function memberName(displayName: string | null | undefined, username: string | null | undefined): string | null {
  if (displayName && displayName.trim() !== "") return displayName;
  if (username && username.trim() !== "") return `@${username}`;
  return null;
}

/** The ids the rollup can name. */
export function namedByRollup(members: readonly ChatCounterpart[]): Set<string> {
  const out = new Set<string>();
  for (const m of members) if (memberName(m.display_name, m.username) !== null) out.add(m.user_id);
  return out;
}

/** The senders nobody has named and nobody has asked about. Empty: do not fetch.
 *  The caller marks the result asked BEFORE fetching. */
export function toAsk(
  messages: readonly Message[],
  named: ReadonlySet<string>,
  asked: ReadonlySet<string>,
  viewerId: string | null,
): Set<string> {
  const out = new Set<string>();
  for (const m of messages) {
    if ((m.message_type ?? "").toLowerCase() === "system") continue;
    const id = m.user_id;
    if (!id || id === viewerId || named.has(id) || asked.has(id)) continue;
    out.add(id);
  }
  return out;
}

/** One row of `GET /chats/:id/members`. */
export interface ChatRosterMember {
  user_id: string;
  role?: string | null;
  username: string | null;
  display_name: string | null;
}

/** userId → name from a roster; the nameless are left out, so they still read "Unknown". */
export function fromRoster(roster: readonly ChatRosterMember[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of roster) {
    const name = memberName(m.display_name, m.username);
    if (name !== null) out[m.user_id] = name;
  }
  return out;
}
